import React, { useMemo } from 'react';
import { MCButton, MCText, View } from '../basic';
import {
  formatDate,
  lang,
  permissions,
  remCss,
  rgba,
  scssVariables,
  userFullName
} from 'src/helpers';
import ShortInfo from '../user/ShortInfo';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import LANG_CONST from 'src/helpers/lang/const';
import moment from 'moment';

import mainModel, { models, STEPS } from 'components/slotSchedule/model';
import errors from 'src/constants/errors';

const model = models[STEPS.BOOKED_LIST];

const ButtonsOfBooked = ({ slot, setSlots }) => {
  const params = {
    auth: mainModel.state.user,
    appointment: slot.appointment
  };

  return (
    <div className={'mc-flex-20'} style={{ justifyContent: 'flex-end' }}>
      {permissions.ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.checkActive(params) && (
        <MCButton
          {...permissions.ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.attributes}
          medium
          hover
          outline
          radius
          dark={slot.resolvedType === 'reschedule'}
          onClick={() => {
            mainModel.setNextStep(STEPS.RESCHEDULE, {
              editId: slot.id,
              selectedDay: slot.startDate,
              type: slot.appointmentType
            });
          }}
        />
      )}
      {permissions.ACTIONS.APPOINTMENTS_CANCEL_BUTTON.checkActive(params) && (
        <MCButton
          {...permissions.ACTIONS.APPOINTMENTS_CANCEL_BUTTON.attributes}
          medium
          hover
          outline
          dark={slot.resolvedType === 'cancel'}
          radius
          onClick={() => {
            setSlots((o) =>
              o.map((i) => (slot.id === i.id ? { ...i, resolvedType: 'cancel' } : i))
            );
          }}
        />
      )}
    </div>
  );
};

const virtualColor = scssVariables['colors-mc-primary'];
const officeColor = scssVariables['colors-mc-orange'];

export const BookedList = ({ onHide, onPrev, onDone }) => {
  model.use();
  const [focusUnresolvedTime, setFocusUnresolvedTime] = React.useState(0);
  const [invalid, setInvalid] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const focusTime = useMemo(() => {
    return moment(focusUnresolvedTime + 500).isSameOrAfter(moment());
  }, [focusUnresolvedTime]);

  return (
    <>
      <div className='mc-modal-header mc-flex'>
        <div className='mc-popup-title mc-grow'>{lang.t(LANG_CONST.titles.resolve_bookings)}</div>
        <MCButton
          label={'Cancel All'}
          quaternary
          light
          onClick={() => {
            model.setState({
              list: model.state.list.map((i) => ({ ...i, resolvedType: 'cancel' }))
            });
          }}
        />
      </div>
      {invalid?.error && <MCText label={invalid.error} />}
      <div className='mc-modal-body mc-grow mc-flex-15-col'>
        {model.state.list.map((slot, bookedIndex) => {
          let type = slot.appointmentType;
          let startDate = slot.startDate;
          let endDate = slot.endDate;

          if (slot.resolvedType === 'reschedule') {
            type = slot.rescheduleType;
            startDate = slot.rescheduleStartDate;
            endDate = slot.rescheduleEndDate;
          }
          return (
            <div
              className={`mc-flex-col mc-radius-5 mc-p20 ${focusTime && !slot.resolvedType ? 'mc-b-primary' : 'mc-b2'}`}
              key={bookedIndex}
            >
              <View style={{ flexDirection: 'row', gap: remCss(15) }}>
                <MCText
                  label={formatDate(startDate) + moment(endDate).format(' - HH:mm')}
                  weight={500}
                />
                <View>
                  <MCText
                    heading
                    label={LANG_CONST.constants.appointment_types[type]}
                    numberOfLines={1}
                    style={{
                      backgroundColor:
                        type === 'virtual' ? rgba(virtualColor, 0.3) : rgba(officeColor, 0.2),
                      paddingHorizontal: 8,
                      borderRadius: 8
                    }}
                    size={14}
                    lineHeight={20}
                    weight={400}
                  />
                </View>
              </View>
              <div className={'mc-grow mc-flex-20 mc-pointer'}>
                <div className={'mc-grow mc-pl20'}>
                  {slot.patientId > 0 && (
                    <ShortInfo
                      avatar={slot.avatar}
                      fullname={userFullName(slot)}
                      description={[
                        slot.email,
                        slot.phoneNumber ? formatPhoneNumberIntl(slot.phoneNumber.toString()) : null
                      ]
                        .filter((c) => !!c)
                        .join('/')}
                    />
                  )}
                </div>
                <ButtonsOfBooked
                  slot={slot}
                  setSlots={(cb) => {
                    model.setState({ list: cb(model.state.list) });
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className='mc-modal-footer' style={{ gap: remCss(20) }}>
        {onPrev && (
          <MCButton
            medium
            light
            radius
            primary
            style={{ width: remCss(100) }}
            label={LANG_CONST.buttons.back}
            onClick={onPrev}
          />
        )}
        <div className={'mc-grow'} />
        <MCButton
          medium
          light
          radius
          primary
          disabled={loading}
          style={{ width: remCss(100) }}
          label={LANG_CONST.buttons.cancel}
          onClick={onHide}
        />
        <MCButton
          medium
          dark
          radius
          primary
          loading={loading}
          disabled={loading}
          style={{ width: remCss(100) }}
          label={LANG_CONST.buttons.save}
          onClick={() => {
            const { list } = model.state;
            const unresolved = list.find((b) => !b.resolvedType);
            const resolvedList = list.map((b) => ({
              id: b.id,
              resolvedType: b.resolvedType,
              ...(b.resolvedType === 'reschedule'
                ? {
                    rescheduleSlotId: b.rescheduleSlotId,
                    rescheduleType: b.rescheduleType,
                    rescheduleStartDate: b.rescheduleStartDate
                  }
                : {})
            }));
            if (unresolved) {
              setFocusUnresolvedTime(Date.now());
            } else if (onDone) {
              setLoading(true);
              onDone(resolvedList, (error, data) => {
                if (error) {
                  setInvalid({ error: errors(error) });
                }
                if (data?.bookedList) {
                  model.setState({
                    list: data.bookedList
                  });
                }
                setLoading(false);
              });
            }
          }}
        />
      </div>
    </>
  );
};
